import cn from 'classnames';
import {useSelector} from 'react-redux';
import {useHistory} from 'react-router';
import {isEmpty, capitalize} from 'lodash';
import React, {useMemo, useCallback} from 'react';
import {FormattedMessage, useIntl} from 'react-intl';
import {Card, CardContent, CardActionArea, Box, Button, Toolbar} from '@material-ui/core';

import {Navigation} from '../Navigation';
import {RootState, useAppDispatch} from '../../../store';
import {PROJECT_SETTINGS} from '../../../config/constants';
import {StudioTextField} from '../../../base-components/StudioTextField';
import {dataPrepareActions, saveMethodUpdate} from '../../../store/dataPrepare';
import {
  DataPrepareType,
  DataPrepareTypeStr,
} from '../../../types/dataset/DatasetGetAnnotationsResponse';

import './MethodView.scss';

export type MethodViewValues = {
  name: string | null;
  description: string | null;
  type: DataPrepareType | null;
};

export type MethodViewProps = {
  projectId?: string;
  inProgress: boolean;
  showRevert: boolean;
  dataPreparationContainerId?: string;
  onForward: () => void;
  onRevert: () => void;
};

const {setName, setDescription, setType} = dataPrepareActions;

export const MethodView = ({
  projectId,
  inProgress,
  showRevert,
  dataPreparationContainerId,
  onForward,
  onRevert,
}: MethodViewProps) => {
  const intl = useIntl();
  const history = useHistory();
  const dispatch = useAppDispatch();

  const name = useSelector((state: RootState) => state.dataPrepare.name);
  const type = useSelector((state: RootState) => state.dataPrepare.type);
  const description = useSelector((state: RootState) => state.dataPrepare.description);
  const dataContainer = useSelector((state: RootState) => state.dataPrepare.containers);

  const annotationData = useSelector(
    (state: RootState) => state.dataPrepare.annotationData
  );

  const isEditing = useMemo(
    () =>
      Boolean(dataPreparationContainerId) ||
      !isEmpty(dataContainer.datasetRelabelingContainer) ||
      !isEmpty(dataContainer.ddaPreparationContainer),
    [dataContainer, dataPreparationContainerId]
  );

  const handleChange = useCallback(
    (updatedFields: Partial<MethodViewValues>) => {
      const newValues = {
        name: updatedFields.name ?? name,
        description: updatedFields.description ?? description,
        type: updatedFields.type ?? type,
      };

      if (updatedFields.name !== undefined) {
        dispatch(setName(newValues.name));
      }

      if (updatedFields.description !== undefined) {
        dispatch(setDescription(newValues.description));
      }

      if (updatedFields.type !== undefined) {
        dispatch(setType(newValues.type));
      }
    },
    [dispatch, name, description, type]
  );

  const internalOnForward = useCallback(async () => {
    if (projectId && annotationData && dataPreparationContainerId) {
      await dispatch(
        saveMethodUpdate({
          projectId,
          resourceId: dataPreparationContainerId,
          answers: [
            {key: 'Dataset.Name', value: name?.trim() || ' '},
            {key: 'Dataset.Description', value: description?.trim() || ' '},
            {
              key: 'Model.Purpose',
              value:
                type === DataPrepareType.CLASSIFICATION
                  ? capitalize(DataPrepareTypeStr.CLASSIFICATION)
                  : type === DataPrepareType.DETECTION
                  ? capitalize(DataPrepareTypeStr.DETECTION)
                  : capitalize(DataPrepareTypeStr.SEGMENTATION),
            },
          ],
        })
      );
    }

    onForward();
  }, [
    name,
    type,
    projectId,
    description,
    annotationData,
    dataPreparationContainerId,
    onForward,
    dispatch,
  ]);

  const isValid = useMemo(() => Boolean(type != null && name && name.trim()), [
    type,
    name,
  ]);
  const disableForward = useMemo(() => !isValid || inProgress, [isValid, inProgress]);

  return (
    <div className="method-view">
      <Toolbar className="data-prepare__header" variant="dense">
        {showRevert ? (
          <Button
            className="data-prepare__previous-btn"
            variant="contained"
            disableElevation
            onClick={onRevert}
          >
            <FormattedMessage id="dataPrep.reselectImages" />
          </Button>
        ) : (
          <Button
            className="data-prepare__previous-btn"
            variant="contained"
            disableElevation
            onClick={() => history.push(`/project/${projectId}/upload`)}
          >
            <FormattedMessage id="form.returnToPreviousPage" />
          </Button>
        )}
        <Box flexGrow={1} />
        <Navigation
          onForward={internalOnForward}
          disableForward={disableForward}
          hideBack
        />
      </Toolbar>
      <div className="method-view__header">
        <h2>
          {intl.formatMessage({
            id: 'dataPrep.methodview.title',
          })}
        </h2>
        <h5>
          {intl.formatMessage({
            id: 'dataPrep.methodview.desc',
          })}
        </h5>
      </div>
      <div className="method-view__form">
        <div className="method-view__cards">
          <Card
            variant="outlined"
            className={cn('method-view__button', {
              selected: type === DataPrepareType.CLASSIFICATION,
            })}
          >
            <CardActionArea
              className="method-view__button_action_area"
              onClick={() =>
                handleChange({
                  type: DataPrepareType.CLASSIFICATION,
                })
              }
              disabled={isEditing && type !== DataPrepareType.CLASSIFICATION}
            >
              <CardContent className="method-view__button_content">
                <h4>
                  {intl.formatMessage({
                    id: 'dataPrep.classification.title',
                  })}
                </h4>
                <p>
                  {intl.formatMessage({
                    id: 'dataPrep.classification.desc',
                  })}
                </p>
              </CardContent>
            </CardActionArea>
          </Card>
          <Card
            variant="outlined"
            className={cn('method-view__button', {
              selected: type === DataPrepareType.DETECTION,
            })}
          >
            <CardActionArea
              className="method-view__button_action_area"
              onClick={() =>
                handleChange({
                  type: DataPrepareType.DETECTION,
                })
              }
              disabled={isEditing && type !== DataPrepareType.DETECTION}
            >
              <CardContent className="method-view__button_content">
                <h4>
                  {intl.formatMessage({
                    id: 'dataPrep.detection.title',
                  })}
                </h4>
                <p>
                  {intl.formatMessage({
                    id: 'dataPrep.detection.desc',
                  })}
                </p>
              </CardContent>
            </CardActionArea>
          </Card>

          <Card
            variant="outlined"
            className={cn('method-view__button', {
              selected: type === DataPrepareType.SEGMENTATION,
            })}
          >
            <CardActionArea
              className="method-view__button_action_area"
              onClick={() =>
                handleChange({
                  type: DataPrepareType.SEGMENTATION,
                })
              }
              disabled={isEditing && type !== DataPrepareType.SEGMENTATION}
            >
              <CardContent className="method-view__button_content">
                <h4>
                  {intl.formatMessage({
                    id: 'dataPrep.segmentation.title',
                  })}
                </h4>
                <p>
                  {intl.formatMessage({
                    id: 'dataPrep.segmentation.desc',
                  })}
                </p>
              </CardContent>
            </CardActionArea>
          </Card>
        </div>
        <StudioTextField
          placeholder="Enter name..."
          onChange={event => handleChange({name: event.target.value})}
          value={name ?? ''}
          label={intl.formatMessage({
            id: 'dataPrep.name',
          })}
          inputProps={{maxLength: PROJECT_SETTINGS.name.maxLength}}
          fullWidth
          required
        />
        <StudioTextField
          placeholder="Enter description..."
          onChange={event => handleChange({description: event.target.value})}
          value={description ?? ''}
          label={intl.formatMessage({
            id: 'dataPrep.description',
          })}
          multiline={PROJECT_SETTINGS.description.multiline}
          fullWidth
          inputProps={{
            className: PROJECT_SETTINGS.description.className,
            maxLength: PROJECT_SETTINGS.description.maxLength,
          }}
        />
      </div>
    </div>
  );
};
